/**
 * Talon XE span table lookup for Fast Rack Ultrabuilder.
 *
 * Data source: simplified CSV exports in span_tables/ (zone R only).
 * Four files split by module length L (metres, along rail direction).
 *
 * Lookup dimensions: mountType × snowBucket × exposure × slopeBucket × wind q1/50.
 * Output: allowable span in metres (0.61 / 0.92 / 1.22).
 */

export type MountType = 'SHEATHING' | 'TRUSS'
export type Exposure = 'Rough' | 'Open'
export type SlopeBucket = '7 to 27' | '27 to 45'

const WIND_COLUMNS = [0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1] as const

type SpanRow = readonly [number, number, number, number, number, number, number, number, number, number]

// [Rough 7–27, Rough 27–45, Open 7–27, Open 27–45]
type SpanBlock = readonly [SpanRow, SpanRow, SpanRow, SpanRow]

interface SnowBucket {
  readonly lower: number
  readonly upper: number
  readonly sheathing: SpanBlock
  readonly truss: SpanBlock
}

interface LRangeTable {
  readonly upperBound: number
  readonly snowBuckets: readonly SnowBucket[]
}

const u = (v: number): SpanRow => [v, v, v, v, v, v, v, v, v, v]

// ── L ≤ 1.8 m ──────────────────────────────────────────────────────────────────
const TABLE_L_1_8: LRangeTable = {
  upperBound: 1.8,
  snowBuckets: [
    {
      lower: 0,
      upper: 3.5,
      sheathing: [
        u(1.22),
        u(1.22),
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92],
      ],
      truss: [u(1.22), u(1.22), u(1.22), u(1.22)],
    },
    {
      lower: 3.5,
      upper: 4.0,
      sheathing: [
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 0.92, 0.92, 0.92, 0.92, 0.92],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92],
        [1.22, 1.22, 1.22, 1.22, 0.92, 0.92, 0.92, 0.92, 0.92, 0.92],
      ],
      truss: [
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 0.61, 0.61, 0.61, 0.61, 0.61],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.61],
        [1.22, 1.22, 1.22, 1.22, 0.61, 0.61, 0.61, 0.61, 0.61, 0.61],
      ],
    },
  ],
}

// ── 1.8 m < L ≤ 2.0 m ──────────────────────────────────────────────────────────
const TABLE_L_2_0: LRangeTable = {
  upperBound: 2.0,
  snowBuckets: [
    {
      lower: 0,
      upper: 3.0,
      sheathing: [
        u(1.22),
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92, 0.92],
      ],
      truss: [u(1.22), u(1.22), u(1.22), u(1.22)],
    },
    {
      lower: 3.0,
      upper: 3.5,
      sheathing: [
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92, 0.92],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92, 0.92, 0.92, 0.92],
      ],
      truss: [
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.61, 0.61],
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.61, 0.61, 0.61, 0.61],
      ],
    },
    {
      lower: 3.5,
      upper: 4.0,
      sheathing: [u(0.92), u(0.92), u(0.92), u(0.92)],
      truss: [u(0.61), u(0.61), u(0.61), u(0.61)],
    },
  ],
}

// ── 2.0 m < L ≤ 2.25 m ─────────────────────────────────────────────────────────
const TABLE_L_2_25: LRangeTable = {
  upperBound: 2.25,
  snowBuckets: [
    {
      lower: 0,
      upper: 3.0,
      sheathing: [
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92, 0.92],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92, 0.92, 0.92],
      ],
      truss: [
        u(1.22),
        u(1.22),
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.61, 0.61],
      ],
    },
    {
      lower: 3.0,
      upper: 4.0,
      sheathing: [u(0.92), u(0.92), u(0.92), u(0.92)],
      truss: [u(0.61), u(0.61), u(0.61), u(0.61)],
    },
  ],
}

// ── 2.25 m < L ≤ 2.5 m ─────────────────────────────────────────────────────────
const TABLE_L_2_5: LRangeTable = {
  upperBound: 2.5,
  snowBuckets: [
    {
      lower: 0,
      upper: 2.5,
      sheathing: [
        u(1.22),
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92, 0.92, 0.92],
        [1.22, 1.22, 1.22, 1.22, 1.22, 1.22, 0.92, 0.92, 0.92, 0.61],
      ],
      truss: [u(1.22), u(1.22), u(1.22), u(1.22)],
    },
    {
      lower: 2.5,
      upper: 3.5,
      sheathing: [
        u(0.92),
        u(0.92),
        u(0.92),
        [0.92, 0.92, 0.92, 0.92, 0.92, 0.92, 0.92, 0.92, 0.92, 0.61],
      ],
      truss: [u(0.61), u(0.61), u(0.61), u(0.61)],
    },
    {
      lower: 3.0,
      upper: 4.0,
      sheathing: [
        [0.92, 0.61, 0.61, 0.61, 0.61, 0.61, 0.61, 0.61, 0.61, 0.61],
        u(0.61),
        u(0.61),
        u(0.61),
      ],
      truss: [u(0.61), u(0.61), u(0.61), u(0.61)],
    },
  ],
}

const L_RANGE_TABLES: readonly LRangeTable[] = [TABLE_L_1_8, TABLE_L_2_0, TABLE_L_2_25, TABLE_L_2_5]

function selectLRangeTable(panelLength_m: number): LRangeTable | null {
  if (panelLength_m <= 0 || panelLength_m > 2.5) return null
  for (const table of L_RANGE_TABLES) {
    if (panelLength_m <= table.upperBound) return table
  }
  return null
}

/**
 * Select the snow bucket matching the given snow load.
 * When the value falls at a boundary between two buckets, uses the higher
 * (more conservative) bucket — the one with the highest `lower` bound that
 * still contains the value.
 */
function selectSnowBucket(table: LRangeTable, snow_kPa: number): SnowBucket | null {
  if (snow_kPa < 0) return null

  let best: SnowBucket | null = null
  for (const bucket of table.snowBuckets) {
    if (snow_kPa >= bucket.lower && snow_kPa <= bucket.upper) {
      if (!best || bucket.lower > best.lower) {
        best = bucket
      }
    }
  }
  return best
}

/**
 * Find the wind column index. Rounds UP to the next column header for
 * conservative lookup when the value falls between headers.
 */
function selectWindIndex(wind_q150: number): number | null {
  if (wind_q150 < WIND_COLUMNS[0] || wind_q150 > WIND_COLUMNS[WIND_COLUMNS.length - 1]) return null
  for (let i = 0; i < WIND_COLUMNS.length; i++) {
    if (wind_q150 <= WIND_COLUMNS[i]) return i
  }
  return null
}

/**
 * Map row index within a SpanBlock: [Rough 7–27, Rough 27–45, Open 7–27, Open 27–45].
 */
function spanBlockRowIndex(exposure: Exposure, slopeBucket: SlopeBucket): number {
  const expOffset = exposure === 'Rough' ? 0 : 2
  const slopeOffset = slopeBucket === '7 to 27' ? 0 : 1
  return expOffset + slopeOffset
}

export function getSlopeBucket(slopeDeg: number): SlopeBucket | null {
  // Module-grid slope can be ~6.999999° when the facet is configured as 7°.
  const slope = Math.round(slopeDeg * 1e4) / 1e4
  if (slope < 7 || slope > 45) return null
  return slope <= 27 ? '7 to 27' : '27 to 45'
}

export interface SpanLookupParams {
  mountType: MountType
  panelLength_m: number
  snow_kPa: number
  wind_q150: number
  exposure: Exposure
  slopeBucket: SlopeBucket
}

export interface SpanLookupResult {
  span_m: number
  span_in: number
}

/**
 * Look up the allowable span from the Talon XE simplified span tables.
 *
 * Returns the span in both metres and inches, or null if inputs are out of range.
 * Used directly for FR-TALON-XE and as the base for metal roof anchor spans
 * (which snap the result to pitch distance before computing anchor qty).
 */
export function retrieveAllowableSpan(params: SpanLookupParams): SpanLookupResult | null {
  const { mountType, panelLength_m, snow_kPa, wind_q150, exposure, slopeBucket } = params

  const table = selectLRangeTable(panelLength_m)
  if (!table) return null

  const bucket = selectSnowBucket(table, snow_kPa)
  if (!bucket) return null

  const windIdx = selectWindIndex(wind_q150)
  if (windIdx === null) return null

  const block = mountType === 'SHEATHING' ? bucket.sheathing : bucket.truss
  const rowIdx = spanBlockRowIndex(exposure, slopeBucket)
  const span_m = block[rowIdx][windIdx]

  return { span_m, span_in: span_m * 39.37 }
}
